<script>
import foamDevData from './dev-data/foam.stats.json'
import foamMillDevData from './dev-data/foam-mill.stats.json'
import millingDevData from './dev-data/milling.stats.json'
import api from '@/helpers/api'
import { tagsMatchQuery } from '@/helpers/foam-tags'
import { sumBy } from 'lodash'
import moment from 'moment'
import toCurrency from '@/helpers/currency'
import yearRangeMixin from './year-range.mixin'

const CHART_COLORS = [
  '#556ee6',
  '#34c38f',
  '#f1b44c',
  '#f46a6a',
  '#50a5f1',
  '#74788d',
  '#e83e8c',
  '#6f42c1',
  '#20c997',
  '#fd7e14',
  '#5b73e8',
  '#343a40'
]

const FOAM_TYPES = ['f32', 'rg50', 'rg70', 'ld24', 's4044', 'mix']
const MILL_TYPES = [
  'mill_seat',
  'mill_mattress',
  'mill_std',
  'mill_seat_back',
  'mill_other'
]

function valueFromStat (stat, field) {
  if (Array.isArray(stat)) {
    return sumBy(stat, row => Number(row[field]) || 0)
  }
  if (stat && typeof stat === 'object') {
    return Number(stat[field]) || 0
  }
  return 0
}

export default {
  name: 'foam-type-stats',
  mixins: [yearRangeMixin],
  watch: {
    year: 'onRefresh',
    startDate: 'onRefresh',
    endDate: 'onRefresh',
    periodType: 'onRefresh',
    data_field: 'onRefresh',
    breakdown: 'onRefresh'
  },
  props: {
    data_field: { type: String, default: 'quantity' },
    title: { type: String },
    showLegend: { type: Boolean, default: false },
    /** `foam` | `milling` | `foam_mill` */
    breakdown: {
      type: String,
      default: 'foam',
      validator: v => ['foam', 'milling', 'foam_mill'].includes(v)
    }
  },
  data () {
    return {
      points: [],
      isLoading: false
    }
  },
  computed: {
    isCurrency () {
      return this.data_field === 'revenue'
    },
    series () {
      return [
        {
          data: this.points.map(p => ({ x: p.label, y: p.value }))
        }
      ]
    },
    total () {
      return sumBy(this.points, 'value')
    },
    legendRows () {
      return this.points.map((row, index) => ({
        key: `${row.label}-${index}`,
        label: row.label,
        value: row.value,
        displayValue: this.isCurrency
          ? toCurrency(row.value)
          : row.value,
        share: this.total
          ? `${Math.round((row.value / this.total) * 100)}%`
          : '0%',
        color: CHART_COLORS[index % CHART_COLORS.length]
      }))
    },
    totalDisplay () {
      return this.isCurrency ? toCurrency(this.total) : this.total
    },
    valueColumnLabel () {
      return this.isCurrency
        ? this.$t('stats.sales.tab_revenue')
        : this.$t('quantity')
    },
    tagQueries () {
      if (this.breakdown === 'milling') {
        return MILL_TYPES.map(mill => ({
          key: mill,
          label: this.$t(`attribute.mill_type.${mill}`),
          tags: [mill]
        }))
      }
      if (this.breakdown === 'foam_mill') {
        const queries = []
        FOAM_TYPES.forEach(foam => {
          MILL_TYPES.forEach(mill => {
            queries.push({
              key: `${foam}|${mill}`,
              label:
                this.$t(`attribute.foam_type_short.${foam}`) +
                ' · ' +
                this.$t(`attribute.mill_type.${mill}`),
              tags: [`foam_${foam}`, mill]
            })
          })
        })
        return queries
      }
      return FOAM_TYPES.map(foam => ({
        key: foam,
        label: this.$t(`attribute.foam_type_short.${foam}`),
        tags: [`foam_${foam}`]
      }))
    },
    chartOptions () {
      const isCurrency = this.isCurrency
      return {
        chart: {
          type: 'treemap',
          toolbar: { show: false },
          animations: { enabled: true, speed: 400 },
          parentHeightOffset: 0,
          sparkline: { enabled: false }
        },
        colors: CHART_COLORS,
        legend: { show: false },
        grid: {
          padding: {
            top: 0,
            right: 4,
            bottom: 8,
            left: 4
          }
        },
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: false
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '11px',
            fontWeight: 500,
            colors: ['#fff']
          },
          formatter (text, opts) {
            const value = opts && opts.value != null ? opts.value : ''
            if (isCurrency) {
              return [text, toCurrency(value)]
            }
            return [text, value]
          }
        },
        tooltip: {
          y: {
            formatter: val => (isCurrency ? toCurrency(val) : `${val}`)
          }
        }
      }
    }
  },
  mounted () {
    this.onRefresh()
  },
  methods: {
    pointsFromDevRows (rows) {
      // Same categories/labels as production (foam_b40 counts as s4044 / RG40).
      return this.tagQueries
        .map(query => {
          const matched = (rows || []).filter(row =>
            tagsMatchQuery(
              row.tags,
              query.tags,
              query.tags.length > 1
            )
          )
          return {
            label: query.label,
            value: Number(
              sumBy(matched, this.data_field).toFixed(this.isCurrency ? 2 : 0)
            )
          }
        })
        .filter(point => point.value > 0)
        .sort((a, b) => b.value - a.value)
    },
    async fetchProductionPoints () {
      const startDate = this.rangeStartDate.toDate()
      const endDate = this.rangeEndDate.toDate()
      const valueField = this.isCurrency ? 'sales_revenue' : 'quantity'

      const results = await Promise.all(
        this.tagQueries.map(query =>
          api.itemTagStatPerWeek
            .fetch({
              start_date: startDate,
              end_date: endDate,
              tags: query.tags,
              // Foam · milling combos must match ALL tags (AND), not any (OR).
              tag_operator: query.tags.length > 1
            })
            .catch(() => null)
        )
      )

      return this.tagQueries
        .map((query, index) => ({
          label: query.label,
          value: Number(
            valueFromStat(results[index], valueField).toFixed(
              this.isCurrency ? 2 : 0
            )
          )
        }))
        .filter(point => point.value > 0)
        .sort((a, b) => b.value - a.value)
    },
    async onRefresh () {
      this.isLoading = true

      try {
        if (process.env.NODE_ENV !== 'production') {
          const source =
            this.breakdown === 'foam_mill'
              ? foamMillDevData
              : this.breakdown === 'milling'
                ? millingDevData
                : foamDevData

          let result = this.filterInRange(
            source,
            this.rangeStartDate,
            this.rangeEndDate
          )
          if (!result.length) {
            result = (source || []).filter(x => {
              const y = moment(x.start_date).year()
              return y === this.year
            })
          }
          if (!result.length) result = source || []
          this.points = this.pointsFromDevRows(result)
        } else {
          this.points = await this.fetchProductionPoints()
        }
      } catch (e) {
        this.points = []
      }

      this.isLoading = false
    }
  }
}
</script>
<template>
  <div class="portal-foam">
    <div v-if="isLoading" class="text-muted small py-3">
      {{ $t('Loading...') }}
    </div>
    <div v-else class="stats-charts__stack">
      <div class="stats-charts__chart">
        <apexchart
          :key="`foam-${breakdown}-${data_field}-${year}-${startDate}-${endDate}`"
          type="treemap"
          width="100%"
          height="320"
          :options="chartOptions"
          :series="series"
        ></apexchart>
      </div>

      <div v-if="showLegend" class="stats-charts__legend">
        <table v-if="legendRows.length">
          <thead>
            <tr>
              <th>{{ $t('dashboard.activity.col_type') }}</th>
              <th class="text-right">{{ valueColumnLabel }}</th>
              <th class="text-right">
                {{ $t('dashboard.activity.col_share') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in legendRows" :key="row.key">
              <td>
                <span
                  class="stats-charts__swatch"
                  :style="{ background: row.color }"
                ></span>
                <span>{{ row.label }}</span>
              </td>
              <td class="text-right stats-charts__qty">
                {{ row.displayValue }}
              </td>
              <td class="text-right text-muted">{{ row.share }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>{{ $t('Total') }}</td>
              <td class="text-right stats-charts__qty">{{ totalDisplay }}</td>
              <td class="text-right text-muted">100%</td>
            </tr>
          </tfoot>
        </table>
        <p v-else class="text-muted small mb-0">
          {{ $t('dashboard.activity.empty_table') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stats-charts__stack {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
}

.stats-charts__chart {
  min-width: 0;
  width: 100%;
  padding: 0;
}

.stats-charts__legend {
  min-width: 0;
  max-width: 40rem;
  padding: 0.5rem 0.25rem 0.75rem;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.72rem;
  }

  th,
  td {
    padding: 0.22rem 0.15rem;
    border-bottom: 1px solid #eef2f7;
    vertical-align: middle;
  }

  th {
    color: #94a3b8;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 0.62rem;
  }

  tfoot td {
    border-bottom: 0;
    padding-top: 0.35rem;
    font-weight: 500;
    color: #0f172a;
  }
}

.stats-charts__swatch {
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  margin-right: 0.3rem;
  vertical-align: middle;
}

.stats-charts__qty {
  color: #0f172a;
  font-weight: 500;
}
</style>
